import * as AllIcons from "@heroicons/react/outline";
import { useState } from "react";
import type { menuItems, ThreeDotsItemKind } from "../Icon/ThreeDotsDropdownMenu";
import { getProjectGroupMenuItems } from "../Icon/threeDotsMenuItems";
import type {
  IProjectGroupElementNestedProject,
  IProjectGroupWithProjects,
} from "../../../features/projectGroups/state/projectGroupsSlice";
import {
  fetchAllCurrentElements,
  getProjectGroupPermissionsFromServer,
  setCurrentProjectGroupPermissions,
  setSelectedProjectGroup,
  setSelectedProjectGroupFromId,
} from "../../../features/projectGroups/state/projectGroupsSlice";
import { useAppDispatch, useAppSelector } from "../../../common/state/hooks";
import {
  getProjectPermissionsFromServer,
  projectsSelector,
  setCurrentProjectFromId,
  setCurrentProjectPermissions,
  setEdited,
} from "../../../features/projects/state/projectsSlice";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ProjectsInProjectGroup from "./ProjectsInProjectGroup";
import DisplayThreeDotsDropdownMenu from "./DisplayThreeDotsDropdownMenu";
import { PROJECT_GROUP_LIST_COMPONENT_ID } from "src/features/projectGroups/components/ProjectGroupList";
import ChevronRightIconButton from "./ChevronRightIconButton";
import { useOpenNewModuleWizard } from "src/features/projectGroups/hooks/useOpenNewModuleWizard";

import { PGThumbnail } from "../PGThumbnail/PGThumbnail";
import { TruncatedText } from "../TruncatedText/TruncatedText";

interface ButtonSquareIconProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  projectGroupWithProjects: IProjectGroupWithProjects;
  handleThreeDotButtonClick: ({
    item,
    itemId,
    action,
  }: {
    item: ThreeDotsItemKind;
    itemId: number;
    action: string;
  }) => void;
  simplified?: boolean;
  openProjectGroup: (projectGroup: IProjectGroupWithProjects) => void;
  setEditedProjectGroupId?: (id: number, projectGroup: IProjectGroupWithProjects) => void;
}

const ButtonSquareIconsProjectGroupCreator = ({
  projectGroupWithProjects,
  handleThreeDotButtonClick,
  simplified,
  openProjectGroup,
  setEditedProjectGroupId,
  ...props
}: ButtonSquareIconProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const pg = {
    s3Url: projectGroupWithProjects.source_thumbnail?.s3_url,
    name: projectGroupWithProjects.name,
    id: projectGroupWithProjects.id,
    elements: [...(projectGroupWithProjects.project_group_elements ?? [])]
      .filter((pge) => pge.project?.is_deleted !== true)
      .sort((a, b) => a.order - b.order),
    authType: projectGroupWithProjects.auth_type,
  };

  const disabledDelete = projectGroupWithProjects.has_existing_build;

  const menuItems: menuItems = getProjectGroupMenuItems(t, disabledDelete);
  let starColor = "stroke-white";

  const [toggleStar, setToggleStar] = useState(false);

  if (toggleStar) {
    starColor = "fill-yellow-500 stroke-yellow-500";
  }

  const getAllProjects = useAppSelector(projectsSelector);

  const handleClick = ({
    project,
    projectGroup,
  }: {
    project: IProjectGroupElementNestedProject;
    projectGroup: IProjectGroupWithProjects;
  }) => {
    dispatch(setSelectedProjectGroupFromId(Number(pg.id)));
    void dispatch(
      fetchAllCurrentElements({
        projectGroupId: Number(pg.id),
        componentId: PROJECT_GROUP_LIST_COMPONENT_ID,
      }),
    );

    dispatch(setCurrentProjectFromId(Number(project.id)));
    dispatch(setEdited());
    navigate(`/courses/${projectGroup.id}/list/${project.id}/graph`);
    dispatch(setSelectedProjectGroup(projectGroup));
    void dispatch(getProjectPermissionsFromServer({ projectId: Number(project.id) })).then(
      (res) => {
        if (!getProjectPermissionsFromServer.fulfilled.match(res)) {
          return;
        }
        dispatch(setCurrentProjectPermissions(res.payload.data));
      },
    );
    void dispatch(getProjectGroupPermissionsFromServer({ projectGroupId: Number(pg.id) })).then(
      (res) => {
        if (!getProjectGroupPermissionsFromServer.fulfilled.match(res)) {
          return;
        }
        dispatch(setCurrentProjectGroupPermissions(res.payload.data));
      },
    );
  };

  const openNewModuleWizard = useOpenNewModuleWizard();
  const createNewProject = ({ projectGroup }: { projectGroup: IProjectGroupWithProjects }) =>
    openNewModuleWizard(projectGroup);

  const pgElementsLength = pg.elements.length > 0;

  const MetaDetails = () => {
    const allProjectsLength = pgElementsLength
      ? `${pg.elements.length} module${pg.elements.length > 1 ? "s" : ""}`
      : "0 modules";

    let prettyAuthType = t("pages.accessTypeTable.passwordLogin");
    if (pg.authType === "simplified") {
      prettyAuthType = t("pages.accessTypeTable.simplifiedLogin");
    }
    if (pg.authType === "none") {
      prettyAuthType = t("pages.accessTypeTable.noLogin");
    }

    return <>{`${prettyAuthType} - ${allProjectsLength}`}</>;
  };

  return (
    <div
      data-testid="project-group"
      data-truncate-trigger
      className={`bg-[#e6e6e6] relative rounded-3xl nyanya ${
        simplified ? "min-w-0 overflow-hidden" : "w-fit min-w-[50.6rem] "
      } font-medium h-[14rem] bg-center bg-cover bg-gray-300 bg-opacity-50 bg-blend-soft-light shadow-md cursor-pointer`}
      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        openProjectGroup(projectGroupWithProjects);
      }}
    >
      <button data-tour="course-card-open" {...props}>
        <div className="pt-2 ml-4 flex grow h-full justify-between ">
          {/* the "Favorites" feature is disabled for now */}
          <div
            className="justify-start hidden" // removed flex because eslint was complaining it was causing a warning with hidden which has the same effect
            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
              event.stopPropagation();
            }}
          >
            <AllIcons.StarIcon
              onClick={() => setToggleStar(!toggleStar)}
              className={`w-6 h-6 ${starColor}`}
            />
          </div>
          <PGThumbnail src={pg.s3Url} title={pg.name} />

          <div
            className={` text-green-500 text-2xl  flex justify-evenly min-w-0 flex-1 ${
              pgElementsLength ? " text-left  flex-col mt-0" : "-mt-5"
            } ml-2`}
          >
            <div className="flex items-center min-w-0">
              <TruncatedText as="p" className="text-3xl max-w-[34rem]" text={pg.name} />
              <div className="relative w-6 h-6 ml-3 flex-shrink-0">
                <ChevronRightIconButton className="w-6 h-6" />
              </div>
            </div>
            <p
              className={`text-sm text-gray-600 font-semibold ${
                pgElementsLength ? "" : "mt-8 ml-3"
              } `}
            >
              <MetaDetails />
            </p>
          </div>
        </div>
      </button>

      <DisplayThreeDotsDropdownMenu
        isEnduser={false}
        menuItems={menuItems}
        pg={projectGroupWithProjects}
        handleThreeDotButtonClick={handleThreeDotButtonClick}
        setEditedProjectGroupId={setEditedProjectGroupId}
      />
      <ProjectsInProjectGroup
        handleClick={handleClick}
        handleThreeDotButtonClick={handleThreeDotButtonClick}
        numberOfRelevantProjectElements={pg.elements.length}
        projectGroupWithProjects={projectGroupWithProjects}
        allProjectsEver={getAllProjects}
        simplified={simplified}
        createNewProject={createNewProject}
      />
    </div>
  );
};

export default ButtonSquareIconsProjectGroupCreator;
